import { useMemo } from 'react';
import { useStore } from '../store/store';
import useSubscriptionStatus from './useSubscriptionStatus';

/**
 * Combines the two enrichment access gates into a single hook for consumers
 * (ContactCard reveal buttons, IndividualDetails Contact Information section,
 * XLSX dialog enrichment fields, action bar in Phase 3b).
 *
 * Gate composition:
 *   1. Cognito group: `profilerAccessStore.isCognitoContactEnrichment`
 *      (set by BE via `enrichUser` based on `contactenrichment` Cognito group
 *      membership, persisted to DynamoDB user row per TD-2188).
 *   2. Per-company: `useSubscriptionStatus().enrichment != null`
 *      (BE omits the `enrichment` block from /subscription-status when the
 *      company's `enrichment_credit_limit` is NULL).
 *
 * When the Cognito gate is closed we short-circuit BEFORE calling
 * useSubscriptionStatus — keeps non-enrichment users from issuing the
 * /subscription-status request.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false, the underlying
 *   /subscription-status fetch is suppressed. Used by ContactCard (TD-2263)
 *   so a card consuming a hoisted `access` prop doesn't ALSO mount its own
 *   fetching hook instance — the list would otherwise fan out N requests and
 *   race the path-global cancelRequest. Rules of Hooks forbid skipping the
 *   hook call entirely, so we gate the fetch instead.
 *
 * @returns {{
 *   enabled: boolean,
 *   remaining: number,
 *   used: number,
 *   limit: number,
 *   limitReached: boolean,
 *   isLoading: boolean,
 *   error: string|null,
 *   refetch: Function|null
 * }}
 *
 * `enabled: false` suppresses this instance's REQUEST, not its data. Since
 * subscription status became one shared snapshot, a disabled instance still
 * reads whatever another consumer loaded, so `enabled` in the returned object
 * can be true even for a caller that passed `enabled: false` — it reports
 * whether the account HAS enrichment, not whether this instance asked. Pass
 * `enabled: false` to avoid a duplicate fetch (the ContactCard fan-out guard),
 * never as a way to force the returned access off.
 *
 * Note (v3 mockup, 2026-05-25): overage UX is dropped entirely from the FE.
 * BE keeps `overage_enabled` on /subscription-status for billing semantics, but
 * the FE never exposes or consumes it. Insufficient credits surface as a grey
 * "on hold" state instead of an overage confirmation modal.
 */
const useEnrichmentAccess = ({ enabled = true } = {}) => {
  const { profilerAccessStore } = useStore();
  const cognitoEnabled = profilerAccessStore.isCognitoContactEnrichment;
  const { enrichment, isLoading, error, refetch } = useSubscriptionStatus({
    enabled: enabled && cognitoEnabled
  });

  return useMemo(() => {
    const enabled = cognitoEnabled && !!enrichment;
    return {
      enabled,
      remaining: enrichment?.remaining ?? 0,
      used: enrichment?.used ?? 0,
      limit: enrichment?.limit ?? 0,
      limitReached: enrichment?.limitReached ?? false,
      isLoading: cognitoEnabled ? isLoading : false,
      error: cognitoEnabled ? error : null,
      refetch: cognitoEnabled ? refetch : null
    };
  }, [cognitoEnabled, enrichment, isLoading, error, refetch]);
};

export default useEnrichmentAccess;
